import { createAsyncThunk } from '@reduxjs/toolkit';

import folderModel from '@/models/folder';
import transactionModel from '@/models/transaction';

export const fetchDocuments = createAsyncThunk(
	'myDocuments/fetchDocuments',
	async (folderId, { rejectWithValue, getState }) => {
		try {
			const {
				currentFolder,
				page,
				searchTerm: fileName
			} = getState().myDocuments;
			const folder = folderId || currentFolder?.id;

			const options = {
				...(page && page > 1 && { page }),
				...(fileName && { fileName }),
				...(folder && { folder })
			};
			const response = await transactionModel.getTransactions(options);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const getFolderById = createAsyncThunk(
	'myDocuments/getFolderById',
	async (id, { rejectWithValue }) => {
		try {
			const response = await folderModel.getFolderById(id);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchFolders = createAsyncThunk(
	'myDocuments/fetchFolders',
	async (_, { rejectWithValue, getState }) => {
		try {
			const { searchFolder } = getState().myDocuments;
			const options = {};
			const folders = {};

			if (searchFolder) {
				options.searchTerm = searchFolder;
			}

			// folders.folder = currentFolder !== {} ? currentFolder : null;

			const response = await folderModel.getFolders(folders, options);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const deleteDocuments = createAsyncThunk(
	'myDocuments/deleteDocuments',
	async ({ ids = [] }, { rejectWithValue, dispatch }) => {
		try {
			let response;

			if (ids.length > 1) {
				response = await transactionModel.batchDelete(ids);
			} else {
				response = await transactionModel.deleteTransaction(ids[0] || null);
			}

			if (!response.success) {
				throw new Error(response.statusText);
			}

			dispatch(fetchDocuments());
			dispatch(fetchFolders());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const deleteFolder = createAsyncThunk(
	'myDocuments/deleteFolder',
	async ({ folderId = null }, { rejectWithValue, dispatch }) => {
		try {
			const response = await folderModel.deleteFolder(folderId);

			if (!response) {
				throw new Error();
			}

			dispatch(fetchFolders());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const createFolder = createAsyncThunk(
	'myDocuments/createFolder',
	async ({ name, parentId }, { rejectWithValue, dispatch }) => {
		try {
			const response = await folderModel.createFolder(name, parentId);

			if (!(response.success || response.insertedId)) {
				throw new Error(response.statusText);
			}

			dispatch(fetchFolders());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const updateFolder = createAsyncThunk(
	'myDocuments/updateFolder',
	async ({ folderId, name }, { rejectWithValue, dispatch, getState }) => {
		try {
			const parentId = getState().myDocuments.isSubfolder
				? getState().myDocuments.parentFolder.id
				: null;

			const response = await folderModel.updateFolder(
				folderId,
				name,
				parentId
			);

			if (!response.success) {
				throw new Error(response.statusText);
			}

			dispatch(getFolderById(folderId));
			dispatch(fetchFolders());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const batchMoveTransactions = createAsyncThunk(
	'myDocuments/batchMoveTransactions',
	async ({ transactions, folder }, { rejectWithValue, dispatch }) => {
		try {
			const response = await folderModel.batchMoveTransactions(
				folder,
				transactions
			);

			if (!response.success) {
				throw new Error(response.statusText);
			}

			dispatch(fetchFolders());
			dispatch(fetchDocuments());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const batchRemoveTransactions = createAsyncThunk(
	'myDocuments/batchRemoveTransactions',
	async ({ transactions }, { rejectWithValue, dispatch }) => {
		try {
			const response =
				await folderModel.batchRemoveTransactions(transactions);

			if (!response.success) {
				throw new Error(response.statusText);
			}

			dispatch(fetchFolders());

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);
